import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api from '../utils/api';

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState(() => {
        // Read from localStorage first for instant apply (avoid flash)
        return localStorage.getItem('theme') || 'light';
    });

    // Apply theme to document on mount and change
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        localStorage.setItem('theme', theme);
    }, [theme]);

    // Sync with server on mount
    useEffect(() => {
        const syncTheme = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) return;
                const response = await api.get('/auth/me');
                const serverTheme = response.data?.user?.theme_preference;
                if (serverTheme && serverTheme !== theme) {
                    setTheme(serverTheme);
                }
            } catch (error) {
                // Silently fail — use localStorage value
            }
        };
        syncTheme();
    }, []);

    const toggleTheme = useCallback(async () => {
        const newTheme = theme === 'light' ? 'dark' : 'light';
        setTheme(newTheme);
        // Persist to server
        try {
            await api.put('/auth/preferences', { theme_preference: newTheme });
        } catch (error) {
            console.error('Failed to save theme preference:', error);
        }
    }, [theme]);

    const setThemeMode = useCallback(async (newTheme) => {
        setTheme(newTheme);
        try {
            await api.put('/auth/preferences', { theme_preference: newTheme });
        } catch (error) {
            console.error('Failed to save theme preference:', error);
        }
    }, []);

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme, setThemeMode }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
